import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Picker } from 'emoji-mart';
import i18nPt from '@emoji-mart/data/i18n/pt.json';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';

// Dataset e sprite são AUTO-HOSPEDADOS (public/emoji/), não usamos CDN:
// - data-pt.json: gerado por scripts/build-emoji-pt.js, com nome e palavras-chave em
//   português (inclusive sem acento), senão a busca só funcionaria em inglês.
// - apple-64.png: spritesheet do conjunto Apple (o mesmo estilo do WhatsApp), para os
//   emojis não dependerem da fonte do sistema (no Windows ela é bem pobre).
const EMOJI_DATA_URL = '/emoji/data-pt.json';
const EMOJI_SHEET_URL = '/emoji/apple-64.png';

// Carrega o dataset uma única vez por sessão (500 KB): só na primeira abertura do picker.
let dataPromise = null;
const carregarDados = () => {
  if (!dataPromise) {
    dataPromise = fetch(EMOJI_DATA_URL).then((r) => r.json());
  }
  return dataPromise;
};

/**
 * direction: para que lado o painel abre a partir do botão.
 *   'down' (padrão) = cresce para baixo. Use quando sobra espaço abaixo do botão,
 *                     como no modal de criar campanha.
 *   'up'            = cresce para cima. Use quando o botão fica na parte de baixo da
 *                     tela, como em Enviar Mensagem, senão o painel é cortado pelo
 *                     Layout (h-screen + overflow-hidden).
 * É fixo por tela de propósito: detectar em tempo de execução se mostrou frágil, e a
 * direção correta de cada tela já é conhecida.
 */
export default function EmojiPicker({ onEmojiSelect, className, direction = 'down' }) {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();
  const pickerRef = useRef(null);   // wrapper (botão + painel), usado no clique-fora
  const mountRef = useRef(null);    // onde o painel do emoji-mart é montado
  // Mantém o callback atual sem precisar remontar o painel a cada render.
  const onSelectRef = useRef(onEmojiSelect);
  onSelectRef.current = onEmojiSelect;

  // Fecha ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Monta o painel do emoji-mart quando abre (e remonta se o tema mudar).
  useEffect(() => {
    if (!isOpen || !mountRef.current) return;
    let cancelado = false;
    const container = mountRef.current;

    carregarDados().then((data) => {
      if (cancelado || !container) return;
      // O tom de pele escolhido fica salvo no navegador e TEM PRECEDÊNCIA sobre a
      // configuração abaixo (o emoji-mart lê `store.get('skin') || props.skin`). Como o
      // seletor foi removido, quem já tinha trocado o tom ficaria preso nele para sempre,
      // sem forma de voltar. Limpamos a preferência para todos voltarem ao padrão.
      try { window.localStorage.removeItem('emoji-mart.skin'); } catch { /* ignora */ }
      const picker = new Picker({
        data,
        skin: 1, // tom padrão (o seletor está desativado)
        i18n: i18nPt,
        set: 'apple',
        theme: theme === 'dark' ? 'dark' : 'light',
        locale: 'pt',
        getSpritesheetURL: () => EMOJI_SHEET_URL,
        previewPosition: 'none',
        // Sem seletor de tom de pele: ele aparecia como uma bolinha colorida (uma amostra
        // de cor em CSS, não um emoji) que destoava no meio dos emojis, e é um recurso de
        // pouquíssimo uso no envio comercial.
        skinTonePosition: 'none',
        navPosition: 'top',
        perLine: 8,
        emojiSize: 24,
        maxFrequentRows: 2,
        searchPosition: 'sticky',
        // NÃO fecha ao escolher: permite inserir vários emojis seguidos (igual WhatsApp).
        onEmojiSelect: (emoji) => {
          if (emoji && emoji.native) onSelectRef.current(emoji.native);
        },
      });
      container.innerHTML = '';
      container.appendChild(picker);

    });

    // Dentro de um Dialog (ex.: criar campanha), o Radix usa react-remove-scroll para
    // travar a rolagem da página. Ele escuta 'wheel'/'touchmove' no document (fase de
    // bolha) e cancela o que não reconhece como área rolável. Como o emoji-mart renderiza
    // em Shadow DOM, o evento chega lá "retargetado" para o elemento host e a lista
    // interna fica invisível para ele, que então bloqueia a rolagem do picker. Impedimos
    // o evento de subir até o document: não cancelamos nada, só evitamos que ele seja
    // cancelado, e a rolagem nativa do picker volta a funcionar.
    const impedirTravaDeRolagem = (e) => e.stopPropagation();
    container.addEventListener('wheel', impedirTravaDeRolagem);
    container.addEventListener('touchmove', impedirTravaDeRolagem);

    return () => {
      cancelado = true;
      container.removeEventListener('wheel', impedirTravaDeRolagem);
      container.removeEventListener('touchmove', impedirTravaDeRolagem);
      container.innerHTML = '';
    };
  }, [isOpen, theme]);

  return (
    <div className={cn('relative', className)} ref={pickerRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="h-9 px-3 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50 hover:text-teal-800 dark:hover:text-teal-200 flex items-center gap-2"
        title="Inserir emoji"
      >
        <span className="text-lg">😊</span>
        <span className="text-sm">Inserir Emoji</span>
      </Button>

      {isOpen && (
        <div
          ref={mountRef}
          className={cn(
            'absolute right-full mr-2 z-50 rounded-lg shadow-lg',
            direction === 'up' ? 'bottom-0' : 'top-0'
          )}
          data-testid="emoji-picker-panel"
        />
      )}
    </div>
  );
}
